import type { Color, GameAction, GameState, Move, PendingBonus, PieceState } from '@ccc/shared-types';
import { generatePseudoLegalForPiece } from './pieces.js';
import { cloneState, findKing, findQueens, pieceAt, piecesOf } from './state.js';
import type { EndConditionResult, RuleModifier, TurnBonus } from './modifiers.js';
import { composeModifiers } from './modifiers.js';
import { fileIndex, fileOf, rankOf, sq } from './board.js';

export interface Engine {
  generateLegalMoves(state: GameState, color?: Color): Move[];
  generateLegalMovesForPiece(piece: PieceState, state: GameState): Move[];
  generateLegalActions(state: GameState, color?: Color): GameAction[];
  applyMove(state: GameState, move: Move): { state: GameState; end: EndConditionResult | null };
  applyAction(state: GameState, action: GameAction): { state: GameState; end: EndConditionResult | null };
  validateMove(state: GameState, move: Move): { ok: true } | { ok: false; reason: string };
  validateAction(state: GameState, action: GameAction): { ok: true } | { ok: false; reason: string };
}

export function createEngine(modifiers: RuleModifier[] = []): Engine {
  const ordered = composeModifiers(modifiers);

  function legalForPiece(piece: PieceState, state: GameState): Move[] {
    if (state.pendingBonus && piece.color !== state.pendingBonus.side) return [];
    let moves = generatePseudoLegalForPiece(piece, state);
    // Modifiers are responsible for their own piece-color / ownership checks
    // inside hooks. Engine never filters by ownerSide — that lets modifiers
    // affect both their own pieces and the opposing side (e.g. Shackling Anchor
    // freezing enemy pieces).
    for (const m of ordered) {
      if (m.hooks.extendLegalMoves) {
        moves = moves.concat(m.hooks.extendLegalMoves(piece, state));
      }
    }
    for (const m of ordered) {
      if (m.hooks.filterLegalMoves) {
        moves = m.hooks.filterLegalMoves(piece, moves, state);
      }
    }
    moves = moves.filter((mv) => allowedCapture(piece, mv, state, ordered));
    moves = filterPendingBonusMoves(piece, moves, state);
    return dedupMoves(moves);
  }

  function legalAll(state: GameState, color?: Color): Move[] {
    const out: Move[] = [];
    const side = color ?? state.pendingBonus?.side ?? state.turn;
    if (state.pendingBonus && side !== state.pendingBonus.side) return [];
    for (const p of piecesOf(state, side)) {
      out.push(...legalForPiece(p, state));
    }
    for (const m of ordered) {
      if (m.hooks.extendSpecialMoves) {
        out.push(...m.hooks.extendSpecialMoves(state));
      }
    }
    return out;
  }

  function legalActions(state: GameState, color?: Color): GameAction[] {
    const side = color ?? state.pendingBonus?.side ?? state.turn;
    const actions: GameAction[] = legalAll(state, side).map((move) => ({ kind: 'move', move }));
    if (!state.pendingBonus) {
      for (const m of ordered) {
        if (m.hooks.extendSpecialActions) {
          actions.push(...m.hooks.extendSpecialActions(state).filter((a) => actionSide(a) === side));
        }
      }
    }
    return actions;
  }

  function validate(state: GameState, move: Move): { ok: true } | { ok: false; reason: string } {
    if (state.status !== 'active') return { ok: false, reason: 'game_not_active' };
    const piece = state.pieces[move.pieceId];
    if (!piece) return { ok: false, reason: 'unknown_piece' };
    const activeSide = state.pendingBonus?.side ?? state.turn;
    if (piece.color !== activeSide) return { ok: false, reason: 'not_your_turn' };
    if (piece.square !== move.from) return { ok: false, reason: 'piece_not_on_from_square' };
    const legal = legalForPiece(piece, state);
    const match = legal.find((m) => sameMove(m, move));
    if (!match) return { ok: false, reason: 'illegal_move' };
    return { ok: true };
  }

  function validateGameAction(state: GameState, action: GameAction): { ok: true } | { ok: false; reason: string } {
    if (action.kind === 'move') return validate(state, action.move);
    if (state.status !== 'active') return { ok: false, reason: 'game_not_active' };
    if (state.pendingBonus) return { ok: false, reason: 'finish_bonus_first' };
    if (action.side !== state.turn) return { ok: false, reason: 'not_your_turn' };
    const legal = legalActions(state, action.side).some((a) => sameAction(a, action));
    return legal ? { ok: true } : { ok: false, reason: 'illegal_action' };
  }

  function apply(state: GameState, move: Move): { state: GameState; end: EndConditionResult | null } {
    const next = cloneState(state);
    const activeBonus = next.pendingBonus;
    const movingPiece = next.pieces[move.pieceId];
    const moveToApply: Move = activeBonus ? { ...move, bonusOf: activeBonus.sourcePly } : { ...move };
    const captured = capturedPieceFor(next, moveToApply);
    const mover = movingPiece?.color ?? next.turn;
    executeMove(next, moveToApply);

    // Capture-reaction & onMoveResolve hooks. Modifiers self-gate inside the hook.
    for (const m of ordered) {
      if (m.hooks.onMoveResolve) {
        const muts = m.hooks.onMoveResolve(moveToApply, next);
        applyMutations(next, muts);
      }
    }

    // Check end conditions after move
    let end: EndConditionResult | null = checkBaseEndConditions(next);
    if (!end) {
      for (const m of ordered) {
        if (!m.hooks.onTurnEnd) continue;
        const r = m.hooks.onTurnEnd(next);
        if (r) {
          end = r;
          break;
        }
      }
    }
    if (end) {
      next.status = 'ended';
      next.endReason = end.reason;
      next.winner = end.winner;
      delete next.pendingBonus;
    } else {
      const granted = activeBonus ? null : grantedBonus(moveToApply, next, mover, captured);
      if (granted) {
        next.pendingBonus = granted;
        next.turn = granted.side;
      } else if (activeBonus) {
        delete next.pendingBonus;
        next.turn = activeBonus.turnAfter;
        if (next.turn === 'white') next.moveNumber += 1;
      } else {
        delete next.pendingBonus;
        next.turn = opponentOf(mover);
        if (next.turn === 'white') next.moveNumber += 1;
      }
    }
    return { state: next, end };
  }

  function applyGameAction(state: GameState, action: GameAction): { state: GameState; end: EndConditionResult | null } {
    if (action.kind === 'move') return apply(state, action.move);
    const next = cloneState(state);
    const key = `stored-tempo-${action.side}`;
    const tempo = Number(next.ruleState[key] ?? 0);
    next.ruleState[key] = tempo + 0.5;
    if (!next.actionHistory) next.actionHistory = next.history.map((move) => ({ kind: 'move', move }));
    next.actionHistory.push(action);
    next.turn = opponentOf(action.side);
    delete next.pendingBonus;
    if (next.turn === 'white') next.moveNumber += 1;
    return { state: next, end: null };
  }

  return {
    generateLegalMoves: legalAll,
    generateLegalMovesForPiece: legalForPiece,
    generateLegalActions: legalActions,
    applyMove: apply,
    applyAction: applyGameAction,
    validateMove: validate,
    validateAction: validateGameAction,
  };

  function allowedCapture(
    attacker: PieceState,
    move: Move,
    state: GameState,
    mods: RuleModifier[],
  ): boolean {
    if (!move.capturedPieceId) return true;
    const target = state.pieces[move.capturedPieceId] ?? pieceAt(state, move.to);
    if (!target) return true;
    for (const mod of mods) {
      if (!mod.hooks.canCapture) continue;
      const v = mod.hooks.canCapture(attacker, target, state);
      if (v === false) return false;
      if (v === true) return true;
    }
    return true;
  }

  function filterPendingBonusMoves(piece: PieceState, moves: Move[], state: GameState): Move[] {
    const pending = state.pendingBonus;
    if (!pending) return moves;
    if (piece.color !== pending.side) return [];
    return moves.filter((move) => {
      if (pending.restrictToPieceIds && !pending.restrictToPieceIds.includes(piece.id)) return false;
      if (!pending.canCapture && move.capturedPieceId) return false;
      if (pending.maxDistance !== undefined && moveDistance(move.from, move.to) > pending.maxDistance) return false;
      return true;
    });
  }

  function grantedBonus(
    move: Move,
    state: GameState,
    mover: Color,
    captured: PieceState | null,
  ): PendingBonus | null {
    const normalNext = opponentOf(mover);
    if (captured) {
      for (const mod of ordered) {
        if (!mod.hooks.reactToCapture) continue;
        const attacker = state.pieces[move.pieceId];
        if (!attacker) continue;
        const reaction = mod.hooks.reactToCapture(captured, attacker, state);
        if (reaction?.bonusFor) {
          return {
            side: reaction.bonusFor,
            kind: 'retaliatory_strike',
            canCapture: false,
            sourcePly: state.history.length,
            turnAfter: normalNext,
          };
        }
      }
    }
    for (const mod of ordered) {
      if (!mod.hooks.turnBonusGranted) continue;
      const bonus = mod.hooks.turnBonusGranted(move, state);
      if (bonus) return toPendingBonus(bonus, state.history.length, normalNext);
    }
    return null;
  }
}

function appliesTo(mod: RuleModifier, color: Color): boolean {
  return mod.ownerSide === 'shared' || mod.ownerSide === color;
}

export function sameMove(a: Move, b: Move): boolean {
  if (a.from !== b.from || a.to !== b.to) return false;
  if (a.promotionTo !== b.promotionTo) return false;
  if ((a.special ?? null) !== (b.special ?? null)) return false;
  return true;
}

function dedupMoves(moves: Move[]): Move[] {
  const seen = new Set<string>();
  const out: Move[] = [];
  for (const m of moves) {
    const k = `${m.from}->${m.to}|${m.promotionTo ?? ''}|${m.special ?? ''}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(m);
  }
  return out;
}

function executeMove(state: GameState, move: Move): void {
  const piece = state.pieces[move.pieceId];
  if (!piece) throw new Error('executeMove: missing piece');

  // Capture (incl. en passant)
  if (move.special === 'en_passant') {
    const captured = move.capturedPieceId ? state.pieces[move.capturedPieceId] : undefined;
    if (captured) delete state.pieces[captured.id];
  } else if (move.capturedPieceId) {
    const captured = state.pieces[move.capturedPieceId];
    if (captured) delete state.pieces[captured.id];
  } else {
    // Defensive: any piece currently on the destination square (e.g. for special moves)
    const occupier = pieceAt(state, move.to);
    if (occupier && occupier.id !== piece.id && occupier.color !== piece.color) {
      delete state.pieces[occupier.id];
    }
  }

  // Castling: move the rook
  if (move.special === 'castle_kingside') {
    const rank = rankOf(move.to);
    const rook = pieceAt(state, sq('h', rank));
    if (rook) {
      rook.square = sq('f', rank);
      rook.hasMoved = true;
    }
  }
  if (move.special === 'castle_queenside') {
    const rank = rankOf(move.to);
    const rook = pieceAt(state, sq('a', rank));
    if (rook) {
      rook.square = sq('d', rank);
      rook.hasMoved = true;
    }
  }

  // Move the piece
  piece.square = move.to;
  piece.hasMoved = true;

  // Promotion
  if (move.promotionTo) {
    piece.type = move.promotionTo;
  }

  state.history.push(move);
  if (!state.actionHistory) state.actionHistory = [];
  state.actionHistory.push({ kind: 'move', move });
}

function capturedPieceFor(state: GameState, move: Move): PieceState | null {
  if (move.capturedPieceId) return state.pieces[move.capturedPieceId] ?? null;
  const piece = state.pieces[move.pieceId];
  const occupier = pieceAt(state, move.to);
  if (piece && occupier && occupier.id !== piece.id && occupier.color !== piece.color) return occupier;
  return null;
}

function checkBaseEndConditions(state: GameState): EndConditionResult | null {
  // King-capture is the default loss condition.
  for (const color of ['white', 'black'] as const) {
    const king = findKing(state, color);
    if (!king) {
      return {
        ended: true,
        winner: color === 'white' ? 'black' : 'white',
        reason: 'king_captured',
      };
    }
  }
  return null;
}

export function applyMutations(state: GameState, mutations: import('./modifiers.js').StateMutation[]): void {
  for (const m of mutations) {
    switch (m.kind) {
      case 'set_flag': {
        const p = state.pieces[m.pieceId];
        if (p) p.customFlags[m.key] = m.value;
        break;
      }
      case 'set_rule_state': {
        state.ruleState[m.cardId] = m.value;
        break;
      }
      case 'remove_piece':
        delete state.pieces[m.pieceId];
        break;
      case 'spawn_piece':
        state.pieces[m.piece.id] = m.piece;
        break;
      case 'transform_piece': {
        const p = state.pieces[m.pieceId];
        if (p) p.type = m.toType;
        break;
      }
    }
  }
}

/** Used by tests and by the alternate-loss-condition rules. */
export function lossConditionFor(
  state: GameState,
  color: Color,
  modifiers: RuleModifier[],
): 'king' | 'queen' {
  for (const mod of modifiers) {
    const o = mod.hooks.overrideLossCondition?.(state, color);
    if (o) return o;
  }
  return 'king';
}

export { findQueens, findKing, fileOf };

function opponentOf(color: Color): Color {
  return color === 'white' ? 'black' : 'white';
}

function toPendingBonus(bonus: TurnBonus, sourcePly: number, turnAfter: Color): PendingBonus {
  const pending: PendingBonus = {
    side: bonus.side,
    kind: bonus.kind,
    canCapture: bonus.canCapture,
    sourcePly,
    turnAfter,
  };
  if (bonus.restrictToPieceIds) pending.restrictToPieceIds = bonus.restrictToPieceIds;
  if (bonus.maxDistance !== undefined) pending.maxDistance = bonus.maxDistance;
  return pending;
}

function actionSide(action: GameAction): Color {
  return action.kind === 'store_tempo' ? action.side : 'white';
}

function sameAction(a: GameAction, b: GameAction): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'move' && b.kind === 'move') return sameMove(a.move, b.move);
  if (a.kind === 'store_tempo' && b.kind === 'store_tempo') return a.side === b.side;
  return false;
}

function moveDistance(from: string, to: string): number {
  const df = Math.abs(fileIndex(fileOf(from)) - fileIndex(fileOf(to)));
  const dr = Math.abs(rankOf(from) - rankOf(to));
  return Math.max(df, dr);
}
